import { Card, CardContent, CardHeader, Grid, CircularProgress, Checkbox, Radio } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { TextField, Button, Tooltip, Typography } from '@material-ui/core';
import ChevronRight from '@material-ui/icons/ChevronRight';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import PropTypes from 'prop-types';
import React, { useState, useContext, useEffect } from 'react';
import { withRouter } from 'react-router';
import { Autocomplete } from '../Autocomplete';
import cn from 'classnames';
import styles from './HazardScores.styles';
import { AppContext } from '../../AppContext';
import { getHazardScores, getHazardScoreById, getClientList, updateHazardScore } from '../../services';

const ANSWER_WEIGHT_ROW_HEIGHT = 35;

const ColoredCheckbox = withStyles({
  root: {
    color: 'var(--jmi-green)',
    '&$checked': {},
  },
  checked: {},
})(props => <Checkbox color="default" {...props} />);

const ColoredRadio = withStyles({
  root: {
    color: 'var(--jmi-green)',
  },
  checked: {},
})(props => <Radio color="default" {...props} />);

const HazardScores = ({ classes }) => {
  // list variables
  const [formHeaders, setFormHeaders] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [answerWeights, setAnswerWeights] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientQuestions, setClientQuestions] = useState([]);
  const [clientAnswerWeights, setClientAnswerWeights] = useState([]);

  // loading
  const [formHeadersLoading, setFormHeadersLoading] = useState(false);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [answerWeightsSaving, setAnswerWeightsSaving] = useState(false);
  const [clientOptionsLoading, setClientOptionsLoading] = useState(false);

  // selected variables
  const [selectedFormHeaderId, setSelectedFormHeaderId] = useState(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [selectedClient, setSelectedClient] = useState(
    null,
    // {
    //   label: 'Cincinnati Insurance (Personal Lines) (CICTE)',
    //   value: 102,
    // },
  );

  // control whether the form is in client edit mode or JMI default edit mode
  const [clientSelectEnabled, setClientSelectEnabled] = useState(false);

  // track modified answer values for the current question
  const [modifiedAnswerValueIds, setModifiedAnswerValueIds] = useState(new Set());

  const { dispatch } = useContext(AppContext);

  //   API REQUESTS
  const getHazardScoresAsync = async () => {
    try {
      setFormHeadersLoading(true);
      const results = await getHazardScores();
      setFormHeaders(results);
    } catch (error) {
      console.log(error);
    }
    setFormHeadersLoading(false);
  };

  const getHazardScoreByIdAsync = async (formHeaderId, clientId = null) => {
    try {
      setQuestionsLoading(true);

      const defaultQuestionsPromise = getHazardScoreById(formHeaderId);
      const clientQuestionsPromise = clientSelectEnabled ? getHazardScoreById(formHeaderId, clientId) : null;

      const [defaultQs, clientQs] = await Promise.all([defaultQuestionsPromise, clientQuestionsPromise]);

      setQuestions(defaultQs.questionAnswerWeights);
      clientQs && setClientQuestions(clientQs.questionAnswerWeights);
    } catch (error) {
      console.log(error);
    }
    setQuestionsLoading(false);
  };

  const getClientListAsync = async () => {
    try {
      setClientOptionsLoading(true);
      const results = await getClientList();
      setClientOptions(
        results.map(c => ({
          label: `${c.name} (${c.code})`,
          value: c.id,
        })),
      );
    } catch (error) {
      console.log(error);
    }
    setClientOptionsLoading(false);
  };

  const updateClientAnswerWeights = async () => {
    // replace the full questionAnswerWeights array with the questionAnswerWeights that have changed
    const questionsToSearch = clientSelectEnabled ? clientQuestions : questions;
    const answerWeightsToSearch = clientSelectEnabled ? clientAnswerWeights : answerWeights;

    const questionIndex = questionsToSearch.findIndex(cq => cq.questionId === selectedQuestionId);
    const selectedFormHeader = formHeaders.find(fh => fh.id === selectedFormHeaderId);

    // const clientQuestionIndex = clientQuestions.findIndex(cq => cq.questionId === selectedQuestionId);
    // const selectedFormHeader = formHeaders.find(fh => fh.id === selectedFormHeaderId);

    /**
     * filter out answer values to send
     * send values that:
     *  1. have been set as modified AND EITHER:
     *    a. have a weight that is not -1
     *    b. have a weight of -1 but also have a weight id (which means the answer weight will be removed)
     */
    const answerValuesToUpdate = answerWeightsToSearch.filter(
      aw => modifiedAnswerValueIds.has(aw.answerValueId) && (aw.weight !== -1 || (aw.weight === -1 && aw.weightId)),
    );

    // send the changes on the current answer weights section
    const changesToSend = {
      id: selectedFormHeader.id,
      propertyTypeId: selectedFormHeader.propertyTypeId,

      questionAnswerWeights: [
        {
          ...questionsToSearch[questionIndex],
          // answersValues: clientAnswerWeights,
          answersValues: answerValuesToUpdate,
        },
      ],
    };

    try {
      setAnswerWeightsSaving(true);

      const result = await updateHazardScore(changesToSend, selectedClient?.value);

      // update the local clientAnswerWeights with the updated stuff from the server (which includes any new IDs if created)
      const tempAnswerWeights = answerWeightsToSearch.slice();

      // go through the results and update the local versions
      result.questionAnswerWeights[0].answersValues.forEach(av => {
        const awIndex = tempAnswerWeights.findIndex(aw => aw.answerValueId === av.answerValueId);
        tempAnswerWeights[awIndex] = {
          ...tempAnswerWeights[awIndex],
          ...av,
        }; // replace it (this will update the weightId if it was 0 before)
      });

      clientSelectEnabled ? setClientAnswerWeights(tempAnswerWeights) : setAnswerWeights(tempAnswerWeights);

      setModifiedAnswerValueIds(new Set());

      // get the index of the question that is being changed and update it locally
      questionsToSearch[questionIndex].answersValues = tempAnswerWeights;

      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'success',
          message: 'Hazard scores saved successfully!',
        },
      });
    } catch (error) {
      console.log(error);
      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'error',
          message: error.message || 'Hazard scores failed to save...',
        },
      });
    }
    setAnswerWeightsSaving(false);
  };

  //   COLUMN RENDER FUNCS
  const renderFormHeaderTile = ({ name, descript, id }, index) => {
    const isSelected = id === selectedFormHeaderId;

    /**
     * disabled if:
     * * anything is loading
     */
    const columnDisabled = formHeadersLoading || questionsLoading || answerWeightsSaving;

    return (
      <div
        key={`form-header-${id}`}
        className={cn(
          classes.columnTile,
          isSelected && classes.columnTileSelected,
          columnDisabled && classes.columnDisabled,
        )}
        onClick={() => setSelectedFormHeaderId(id)}
      >
        <div className={cn('row-between-center')}>
          <section className={cn('col-center-start text-left')}>
            <div>{name}</div>
            <div style={{ fontSize: '0.8em', color: isSelected ? null : '#AAA' }}>{descript}</div>
          </section>
        </div>
        <ChevronRight />
      </div>
    );
  };

  const renderQuestionTile = ({ question, questionId, answersValues = [] }, index) => {
    const isSelected = questionId === selectedQuestionId;

    /**
     * disabled if:
     * * a form header is not selected
     * * anything is loading
     */
    const columnDisabled =
      selectedFormHeaderId === null || formHeadersLoading || questionsLoading || answerWeightsSaving;

    return (
      <div
        key={`question-${questionId}`}
        className={cn(
          classes.columnTile,
          isSelected && classes.columnTileSelected,
          columnDisabled && classes.columnDisabled,
        )}
        onClick={() => setSelectedQuestionId(questionId)}
      >
        <div className={cn('row-between-center')}>
          <section className={cn('col-center-start text-left')}>
            <div>{question}</div>
            <div style={{ fontSize: '0.8em', color: isSelected ? null : '#AAA' }}>{answersValues.length} answers</div>
          </section>
        </div>
        <ChevronRight />
      </div>
    );
  };

  const renderAnswerWeightTileJmiDefaultMode = ({ answerValueId, value, weight, weightId }, index) => {
    /**
     * disabled if:
     * * a form header is not selected
     * * anything is loading
     */
    const columnDisabled =
      selectedFormHeaderId === null || formHeadersLoading || questionsLoading || answerWeightsSaving;

    return (
      <div
        className={cn('row-between-center', columnDisabled && classes.columnDisabled)}
        style={{ borderBottom: '1px solid #AAA', paddingBottom: 10, marginBottom: 10 }}
        key={`answer-${answerValueId}-${index}`}
      >
        <div
          className={cn('row-between-center flex-1')}
          style={{
            minHeight: ANSWER_WEIGHT_ROW_HEIGHT,
          }}
        >
          <div className={'flex-1 text-left'}>{value}</div>
          <input
            type={'number'}
            value={weight}
            // disabled={!clientHasOverride}
            className={cn(
              'flex-1',
              classes.input,
              classes.textInput,
              // !!error && classes.textInputError,
              // !clientHasOverride && classes.disabledText,
            )}
            style={{
              width: 50,
            }}
            onChange={handleDefaultAnswerWeightChange(answerValueId, index)}
          />
        </div>
      </div>
    );
  };

  const renderAnswerWeightTileClientMode = ({ answerValueId, value, weight, weightId }, index) => {
    // const hasRecommendation = categoriesWithActiveRecommendation.includes(recommendationCategoryId);
    
    const clientAnswerValue = clientAnswerWeights.find(x=> x.answerValueId == answerValueId);
    const clientHasOverride = clientAnswerValue.hasOverride;

    /**
     * disabled if:
     * * a form header is not selected
     * * anything is loading
     */
    const columnDisabled =
      selectedFormHeaderId === null || formHeadersLoading || questionsLoading || answerWeightsSaving;

    return (
      <div
        className={cn('row-between-center', columnDisabled && classes.columnDisabled)}
        style={{ borderBottom: '1px solid #AAA', paddingBottom: 10, marginBottom: 10 }}
        key={`answer-${answerValueId}-${index}`}
      >
        <div
          className={cn('row-between-center flex-1')}
          style={{
            minHeight: ANSWER_WEIGHT_ROW_HEIGHT,
            borderRight: '1px solid #AAA',
            paddingRight: 10,
            marginRight: 10,
          }}
        >
          <div className={'flex-1 text-left'}>{value}</div>
          <div className={'flex-1 text-right'}>{weight}</div>
        </div>

        {/* Client overrides */}
        <div className={cn('row-between-center flex-1')} style={{ height: ANSWER_WEIGHT_ROW_HEIGHT }}>
          {/* add/remove icon */}
          <div style={{ marginRight: 10, marginTop: 5 }}>
            {clientHasOverride ? (
              <DeleteIcon
                className={classes.clientConfigAddRemoveIcon}
                style={{ color: 'red' }}
                onClick={() => toggleClientAnswerWeightOverride(clientAnswerValue.answerValueId, false)}
              />
            ) : (
              <AddIcon
                className={classes.clientConfigAddRemoveIcon}
                style={{ color: 'lightgreen' }}
                onClick={() => toggleClientAnswerWeightOverride(clientAnswerValue.answerValueId, true)}
              />
            )}
          </div>

          {clientHasOverride && (
            <React.Fragment>
              {/* <div className={cn('flex-1 text-left mr-5', !clientHasOverride && classes.disabledText)}>{value}</div> */}
              <input
                type={'number'}
                value={clientAnswerValue.weight}
                // disabled={!clientHasOverride}
                className={cn(
                  'flex-1',
                  classes.input,
                  classes.textInput,
                  // !!error && classes.textInputError,
                  // !clientHasOverride && classes.disabledText,
                )}
                style={{
                  width: 50,
                }}
                onChange={handleClientAnswerWeightChange(clientAnswerValue.answerValueId, index)}
              />
            </React.Fragment>
          )}
        </div>
      </div>
    );
  };

  // other logic
  const addQuestionAnswerWeightToModifiedList = answerValueId => {
    setModifiedAnswerValueIds(modifiedAnswerValueIds.add(answerValueId));
  };

  // STATE MODIFIERS
  const handleDefaultAnswerWeightChange = answerValueId => evt => {
    const answerValueIndex = answerWeights.findIndex(aw => aw.answerValueId === answerValueId);

    const tempAnswerWeights = answerWeights.slice();
    tempAnswerWeights[answerValueIndex].weight = evt.target.value;

    setAnswerWeights(tempAnswerWeights);
    addQuestionAnswerWeightToModifiedList(tempAnswerWeights[answerValueIndex].answerValueId);
  };

  const handleClientAnswerWeightChange = answerValueId => evt => {
    const answerValueIndex = clientAnswerWeights.findIndex(aw => aw.answerValueId === answerValueId);

    const tempClientAnswerWeights = clientAnswerWeights.slice();
    tempClientAnswerWeights[answerValueIndex].weight = evt.target.value;

    setClientAnswerWeights(tempClientAnswerWeights);
    addQuestionAnswerWeightToModifiedList(tempClientAnswerWeights[answerValueIndex].answerValueId);
  };

  const toggleClientAnswerWeightOverride = (answerValueId, show) => {
    const answerValueIndex = clientAnswerWeights.findIndex(aw => aw.answerValueId === answerValueId);

    const tempClientAnswerWeights = clientAnswerWeights.slice();
    tempClientAnswerWeights[answerValueIndex].weight = show ? 0 : -1;
    tempClientAnswerWeights[answerValueIndex].hasOverride = show;

    setClientAnswerWeights(tempClientAnswerWeights);
    addQuestionAnswerWeightToModifiedList(tempClientAnswerWeights[answerValueIndex].answerValueId);
  };

  const toggleClientSelectedEnabled = evt => {
    const newStatus = evt.target.checked;

    !newStatus && setSelectedClient(null);

    // clear old stuff out first before enabling client mode so the client render does render first
    setSelectedFormHeaderId(null);
    resetQuestionsAndAnswerWeights();

    setClientSelectEnabled(newStatus);
  };

  const resetQuestionsAndAnswerWeights = () => {
    setQuestions([]);
    setClientQuestions([]);
    setAnswerWeights([]);
    setClientAnswerWeights([]);

    setSelectedQuestionId(null);
  };

  // EFFECTS
  useEffect(() => {
    // clear questions and answer weight columns
    resetQuestionsAndAnswerWeights();

    if (selectedFormHeaderId) {
      // fetch questions for this form header
      getHazardScoreByIdAsync(selectedFormHeaderId, selectedClient?.value);
    }
  }, [selectedFormHeaderId]);

  useEffect(() => {
    if (selectedQuestionId) {
      // get the answer values from the question (JMI defaults)
      const selectedQuestion = questions.find(q => q.questionId === selectedQuestionId);
      console.log(selectedQuestion.answersValues.map(av => ({ ...av })));
      setAnswerWeights(selectedQuestion.answersValues.map(av => ({ ...av })));

      if (clientSelectEnabled) {
        // get the answer values from the question (client values)
        const selectedClientQuestion = clientQuestions.find(q => q.questionId === selectedQuestionId);

        setClientAnswerWeights(
          selectedClientQuestion.answersValues.map(av => ({
            ...av,
            hasOverride: !!av.weightId,
            // hasOverride: !!av.weight && av.weight !== -1,
          })),
        );
      }

      // reset the modified answer weights to track this section
      setModifiedAnswerValueIds(new Set());
    }
  }, [selectedQuestionId]);

  useEffect(() => {
    // reset the selected question when a new client is selected
    resetQuestionsAndAnswerWeights();

    // reset all selected IDs so that flow starts over
    setSelectedFormHeaderId(null);
    setSelectedQuestionId(null);
  }, [selectedClient]);

  useEffect(() => {
    getHazardScoresAsync();
    getClientListAsync();
  }, []);

  return (
    <Grid item xs={12} className={classes.container}>
      <Card className={cn('flex-1', 'flex-column', 'border-left-green')}>
        <CardHeader
          className={classes.cardHeader}
          titleTypographyProps={{ className: classes.cardHeaderTitle }}
          title={
            <div className="row-between-center">
              <div className="flex-2">
                Customize Hazard Scores
                {(formHeadersLoading || questionsLoading || answerWeightsSaving) && (
                  <CircularProgress size={20} style={{ color: 'var(--jmi-blue)', marginLeft: 20 }} />
                )}
              </div>
              <div className="flex-3 row-end-center">
                <div style={{ width: 130 }}>Client Mode</div>
                <ColoredCheckbox
                  style={{ marginRight: 15 }}
                  checked={clientSelectEnabled}
                  onChange={toggleClientSelectedEnabled}
                />
                <Autocomplete
                  loading={clientOptionsLoading}
                  options={clientOptions}
                  onChange={setSelectedClient}
                  value={selectedClient}
                  placeholder={
                    clientSelectEnabled ? 'Select a client before continuing...' : 'Edit the JMI default scores below'
                  }
                  disabled={!clientSelectEnabled}
                />
              </div>
            </div>
          }
        />
        <CardContent
          className={classes.table}
          style={{
            pointerEvents: (clientSelectEnabled && !selectedClient) || answerWeightsSaving ? 'none' : 'auto',
            opacity: !clientSelectEnabled || !!selectedClient ? 1 : 0.5,
          }}
        >
          {/* first column - recommendation categories */}
          <div className={cn(classes.tableColumn)}>
            <div className={classes.tableColumnHeader}>Form Headers</div>
            <div className={classes.columnListContainer}>{formHeaders.map(renderFormHeaderTile)}</div>
          </div>

          {/* second column - recommendation library */}
          <div
            className={cn(
              classes.tableColumn,
              //   selectedCategoryId === null && classes.columnDisabled
            )}
          >
            <div className={classes.tableColumnHeader}>Questions</div>
            {questions
              .sort((a, b) => {
                if (a.question < b.question) {
                  return -1;
                }

                if (a.question > b.question) {
                  return 1;
                }
                return 0;
              })
              .map(renderQuestionTile)}
          </div>

          {/* third column - JMI default hazard scores */}
          <div
            className={cn(
              classes.tableColumn,
              classes.recommendationFormColumn,
              //   [selectedCategoryId, selectedLibraryId].includes(null) && classes.columnDisabled,
              //   (isSaving || isRemoving) && classes.columnDisabled,
            )}
            style={{
              flex: clientSelectEnabled ? 2 : 1,
            }}
          >
            <div className={cn(classes.tableColumnHeader, 'row-center-center')}>
              <div className="flex-1">JMI Defaults</div>
              {clientSelectEnabled && <div className="flex-1">Client Overrides</div>}
            </div>
            <div className={classes.recommendationFormContainer}>
              {answerWeights.map(
                clientSelectEnabled ? renderAnswerWeightTileClientMode : renderAnswerWeightTileJmiDefaultMode,
              )}

              {!!answerWeights.length && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={updateClientAnswerWeights}
                  className={cn('light-blue-background')}
                  style={{ marginTop: 10 }}
                >
                  {answerWeightsSaving ? <CircularProgress color="#fff" size={18} /> : 'Save Changes'}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </Grid>
  );
};

HazardScores.propTypes = {
  classes: PropTypes.object.isRequired,
};
export default withStyles(styles)(withRouter(HazardScores));
